import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, HStack } from '@chakra-ui/react';

const PARTBAY_BLUE = '#0071E3';

/** Portal target id rendered by CreateAccount inside the onboarding-phase chrome. */
export const STEP_FOOTER_PORTAL_ID = 'onboarding-step-footer-portal';

interface ActionSpec {
	label: string;
	onClick: () => void;
	disabled?: boolean;
	isLoading?: boolean;
	loadingText?: string;
}

export interface StepFooterProps {
	/** Renders on the LHS of the action bar — typically the standard "Back" button. */
	back?: {
		label?: string;
		onClick: () => void;
		disabled?: boolean;
		isLoading?: boolean;
	};
	/** Optional buttons rendered between Back and the primary action (e.g. Skip, Back to Setup). */
	secondaryActions?: Array<ActionSpec & { variant?: 'outline' | 'ghost' }>;
	/** Primary call-to-action — rendered as the rightmost solid button. */
	primary?: ActionSpec;
}

const FooterContent = ({ back, secondaryActions, primary }: StepFooterProps) => (
	<HStack spacing={3} w="full" align="center" justify="space-between">
		<Box>
			{back && (
				<Button
					variant="outline"
					size="sm"
					onClick={back.onClick}
					isDisabled={back.disabled}
					isLoading={back.isLoading}
				>
					{back.label ?? 'Back'}
				</Button>
			)}
		</Box>
		<HStack spacing={3}>
			{secondaryActions?.map((action, idx) => (
				<Button
					key={`${action.label}-${idx}`}
					variant={action.variant ?? 'ghost'}
					color={action.variant === 'outline' ? undefined : 'gray.500'}
					size="sm"
					onClick={action.onClick}
					isDisabled={action.disabled}
					isLoading={action.isLoading}
					loadingText={action.loadingText}
				>
					{action.label}
				</Button>
			))}
			{primary && (
				<Button
					bg={PARTBAY_BLUE}
					color="white"
					_hover={{ bg: '#3141a5' }}
					size="sm"
					onClick={primary.onClick}
					isDisabled={primary.disabled}
					isLoading={primary.isLoading}
					loadingText={primary.loadingText}
				>
					{primary.label}
				</Button>
			)}
		</HStack>
	</HStack>
);

/**
 * Renders the step's action buttons.
 *
 * Inside the onboarding flow, `CreateAccount` mounts a fixed footer slot identified by
 * `STEP_FOOTER_PORTAL_ID` and `StepFooter` portals into it so every step shares the same
 * action bar position. Outside the onboarding chrome (e.g. when an onboarding step
 * component is reused inside the Settings → Locations "Add Location" modal), no such
 * portal target exists; in that case we render the buttons inline so callers don't need
 * to know which context they're in.
 */
export const StepFooter = (props: StepFooterProps) => {
	const [container, setContainer] = useState<HTMLElement | null>(null);
	const [hasMounted, setHasMounted] = useState(false);

	useEffect(() => {
		setContainer(document.getElementById(STEP_FOOTER_PORTAL_ID));
		setHasMounted(true);
	}, []);

	if (container) {
		return createPortal(<FooterContent {...props} />, container);
	}

	// Avoid SSR/first-paint flicker: only render the inline fallback after we've
	// confirmed the portal target isn't available.
	if (!hasMounted) return null;

	return (
		<Box
			pt={4}
			mt={2}
			borderTopWidth={1}
			borderColor="gray.200"
			width="100%"
		>
			<FooterContent {...props} />
		</Box>
	);
};
